import {
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Switch,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { FaSave } from "react-icons/fa";
import {
  getAllParameters,
  lockEditing,
  lockEditingHistory,
  lockEditingStatus,
  updateParameters,
} from "../../../server/server";
import HelpButton from "../../../common/HelpButton";
import HelpPageModal from "../../modals/HelpPageModal";
import { generateYears, notif } from "../../utils/util";
import { DataGrid, GridColumns } from "@mui/x-data-grid";
import { set } from "date-fns";

interface Parameter {
  id: string;
  name: string;
  value: string;
  description: string;
  isVisible: boolean;
}

function LockEditing() {
  const [editingLockHistory, setEditingLockHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [historyPageSize, setHistoryPageSize] = useState(6);
  const [historyRowCount, setHistoryRowCount] = useState(6);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(true);

  const [editingCurrent, setEditingCurrent] = useState(false);
  const [helpModal, setHelpModal] = useState(false);

  const closeHelpModal = () => {
    setHelpModal(false);
  };

  useEffect(() => {
    fetchCurrentStatusData();
    fetchEditingLockHistory();
  }, []);

  const fetchCurrentStatusData = async () => {
    const response = await lockEditingStatus();
    if (response.status === 200) setEditingCurrent(response.data.isActive);
    setLoadingStatus(false);
  };

  const fetchEditingLockHistory = async (
    page = historyPage,
    pageSize = historyPageSize,
  ) => {
    const limit = pageSize;
    const offset = page * pageSize;
    const response = await lockEditingHistory(limit, offset);
    if (response.status === 200) {
      setEditingLockHistory(response.data?.items || response.data || []);
      setHistoryRowCount(response.data?.total || response.data?.length || 0);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    const resp = await lockEditing({ isActive: editingCurrent });

    if (resp.status === 201) {
      notif(
        `Планирање успешно ${editingCurrent ? "закључано" : "откључано"}`,
        "success",
      );
      fetchEditingLockHistory();
      setConfirmModalOpen(false);
    } else if (resp.status === 409) {
      notif(
        "Да би изменили статус закључавања, прво морате да промените стање кликом на прекидач",
        "error",
      );
      setConfirmModalOpen(false);
    } else {
      notif("Дошло је до грешке", "error");
    }
  };

  const setPageAndCall = (page: any) => {
    setHistoryPage(page);
    fetchEditingLockHistory(page, historyPageSize);
  };

  const columns = React.useMemo<GridColumns<any>>(
    () => [
      {
        sortable: false,
        field: "changedAt",
        headerName: "Датум промене",
        maxWidth: 200,
        minWidth: 60,
        flex: 1,
      },
      {
        sortable: false,
        field: "isActiveFrom",
        headerName: "Промењено са",
        flex: 1,
        valueGetter: (params) =>
          !params.row.isActiveFrom ? "Откључано" : "Закључано",
      },
      {
        sortable: false,
        field: "isActiveTo",
        headerName: "Промењено на",
        flex: 1,
        valueGetter: (params) =>
          !params.row.isActiveTo ? "Откључано" : "Закључано",
      },
      {
        sortable: false,
        field: "userId",
        headerName: "ИД корисника",
        maxWidth: 120,
        minWidth: 80,
        flex: 1,
      },
      {
        sortable: false,
        field: "userName",
        headerName: "Корисничко име",
        maxWidth: 160,
        minWidth: 160,
        flex: 1,
      },
    ],
    [],
  );

  return (
    <div className="py-10 px-2 overflow-auto gray-small-scroll h-full">
      <div className="flex gap-6 justify-between pr-10">
        <h1 className="text-[#04438b] text-4xl font-bold">
          Конфигурација закључавања планирања
        </h1>
        <HelpButton onClick={() => setHelpModal(true)} />
      </div>
      <div className="rounded-lg border border-[#81848f3d] w-fit p-8 pb-4 mt-8 gap-2">
        <p className="text-[#404040] text-base mt-8">Закључај планирање</p>
        <div className="flex flex-row items-center gap-2 justify-center">
          <Switch
            checked={editingCurrent}
            onChange={() => setEditingCurrent(!editingCurrent)}
          />
          <p className="w-[160px] text-md text-[#404040]">
            {editingCurrent ? "Закључано" : "Откључано"}
          </p>
        </div>

        <div className="w-full flex justify-end mt-12 items-center">
          <Button
            disabled={loadingStatus}
            variant="contained"
            sx={{ backgroundColor: "#0F2765" }}
            endIcon={<FaSave />}
            onClick={() => setConfirmModalOpen(true)}
          >
            Потврди
          </Button>
        </div>

        <Dialog
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
        >
          <DialogTitle>Сачувајте промене закључавања</DialogTitle>
          <DialogContent>
            Да ли сте сигурни да желите да сачувате промене закључавања?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmModalOpen(false)}>Откажи</Button>
            <Button onClick={handleSave} variant="contained" color="primary">
              Потврди
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <div className="mt-8">
        <h2 className="text-[#404040] text-2xl font-bold">
          Историја закључавања
        </h2>
        <DataGrid
          rowCount={historyRowCount}
          loading={loading}
          pagination
          disableColumnMenu
          paginationMode="server"
          onPageChange={(newPage) => {
            setPageAndCall(newPage);
          }}
          onPageSizeChange={(newPageSize) => setHistoryPageSize(newPageSize)}
          page={historyPage}
          pageSize={historyPageSize}
          rows={editingLockHistory}
          columns={columns}
          autoHeight={true}
          getRowHeight={() => "auto"}
          style={{ zIndex: 0 }}
          rowsPerPageOptions={[5, 10, 15]}
          disableSelectionOnClick
          components={{
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                Не постоји историја измена закључавања НПАА
              </Stack>
            ),
            NoResultsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                Не постоји историја измена закључавања НПАА
              </Stack>
            ),
          }}
        />
      </div>
      <HelpPageModal
        modalIsOpen={helpModal}
        closeModal={closeHelpModal}
        name={"locking"}
      />
    </div>
  );
}

export default LockEditing;
